import { useEngineBus } from '../hooks/useEngineBus.js';

/**
 * @file THE INTERACTION PROMPT: one line, bottom centre, telling the player
 * which key does the thing they are standing in front of ("F — Enter").
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * BUS CONTRACT — `'hud:prompt'`
 * ─────────────────────────────────────────────────────────────────────────────
 * ```
 * bus.emit('hud:prompt', { text: 'F — Enter' })   // show
 * bus.emit('hud:prompt', { text: null })          // hide
 * ```
 * The emitter publishes a STATE, not an edge, so `useEngineBus` is the right
 * hook: its trailing-edge throttle always lands on the last payload, and
 * walking in and out of a doorway can therefore never leave the wrong line up.
 *
 * It sits BOTTOM CENTRE — the one strip of the HUD nothing else claims (the
 * health slab and minimap own bottom-left, the objective panel and the perf
 * badge own bottom-right), and it is directly under where the player is
 * standing. No z-index: it is deliberately UNDER `FadeOverlay`, so walking
 * through a door takes the prompt with the rest of the screen.
 *
 * @param {Object} props
 * @param {import('../../engine/core/Engine.js').Engine|null} props.engine
 */
export default function Prompt({ engine }) {
  const payload = useEngineBus(engine, 'hud:prompt', { throttle: 80 });
  const text = typeof payload?.text === 'string' ? payload.text : '';
  if (!text) return null;

  return (
    <div className="pointer-events-none absolute inset-x-0 bottom-16 flex select-none justify-center">
      <div className="rounded-[3px] border border-hairline bg-void/80 px-3.5 py-2 font-hud text-[13px] uppercase tracking-[0.18em] text-hud backdrop-blur-sm">
        {text}
      </div>
    </div>
  );
}
